/**
 * Recommendations API Service
 *
 * Functions for interacting with the Recommendations API endpoints.
 * Recommendations are generated after a note reaches COMPLETED status
 * via the RECOMMEND pipeline stage (post-pipeline enrichment).
 *
 * Response mapping: The API returns snake_case fields (Python convention)
 * but the frontend interfaces use camelCase (TypeScript convention).
 * Each service function maps the raw API response to the typed interface.
 * The rec_type field is also uppercased to match RecommendationType enum.
 */

import api from "./api";
import type {
  RecommendationListResponse,
  Recommendation,
  RecommendationRating,
  RecommendationType,
  RecommendationQualityStats,
} from "../types/recommendation";

// ── Response Mappers ─────────────────────────────────────────────────
// The API returns snake_case (Python) but our interfaces use camelCase (TS).
// These mappers bridge the contract gap at the service boundary — the only
// place where wire format meets application types.

/** Map a single recommendation from API snake_case to interface camelCase */
function mapRecommendation(raw: Record<string, unknown>): Recommendation {
  return {
    id: raw.id as string,
    noteId: (raw.note_id ?? raw.noteId) as string,
    type: (
      (raw.rec_type ?? raw.type) as string
    ).toUpperCase() as RecommendationType,
    content: (raw.content ?? {}) as Record<string, unknown>,
    sourceNoteIds: (raw.source_note_ids ?? raw.sourceNoteIds ?? []) as string[],
    modelName: (raw.model_name ?? raw.modelName ?? "unknown") as string,
    rating: (raw.rating ?? null) as RecommendationRating | null,
    ratedAt: (raw.rated_at ?? raw.ratedAt ?? null) as string | null,
    createdAt: (raw.created_at ?? raw.createdAt ?? "") as string,
  };
}

/** Map the list response wrapper (note_id → noteId) */
function mapListResponse(
  raw: Record<string, unknown>,
): RecommendationListResponse {
  const items = (raw.items ?? []) as Record<string, unknown>[];
  return {
    noteId: (raw.note_id ?? raw.noteId) as string,
    total: (raw.total ?? 0) as number,
    items: items.map(mapRecommendation),
  };
}

/** Map quality stats (total_rated → totalRated, avg_rating → avgRating, etc.) */
function mapStats(raw: Record<string, unknown>): RecommendationQualityStats {
  return {
    total: (raw.total ?? 0) as number,
    totalRated: (raw.total_rated ?? raw.totalRated ?? 0) as number,
    avgRating: (raw.avg_rating ?? raw.avgRating ?? 0) as number,
    distribution: (raw.distribution ?? {}) as Record<
      RecommendationRating,
      number
    >,
    avgByType: (raw.avg_by_type ?? raw.avgByType ?? {}) as Record<
      string,
      number
    >,
  };
}

// ── API Functions ────────────────────────────────────────────────────

/**
 * Get recommendations for a note.
 *
 * @param noteId - UUID of the completed note
 * @returns List of recommendations with their current ratings
 */
export async function getRecommendations(
  noteId: string,
): Promise<RecommendationListResponse> {
  const response = await api.get<Record<string, unknown>>(
    `/recommendations/${noteId}`,
  );
  return mapListResponse(response.data);
}

/**
 * Rate a recommendation.
 *
 * Idempotent — calling again with a different rating overwrites the previous one.
 * Rating 3 ("excellent") triggers golden set promotion in the flywheel.
 *
 * @param recId - UUID of the recommendation
 * @param rating - 0-3 rating value
 * @returns Updated recommendation
 */
export async function rateRecommendation(
  recId: string,
  rating: RecommendationRating,
): Promise<Recommendation> {
  const response = await api.post<Record<string, unknown>>(
    `/recommendations/${recId}/rate`,
    { rating },
  );
  return mapRecommendation(response.data);
}

/**
 * Get aggregated recommendation quality stats.
 *
 * Used by the assessment dashboard to monitor recommendation quality
 * across all notes and types.
 *
 * @returns Quality metrics: total, rated count, avg rating, distribution
 */
export async function getRecommendationStats(): Promise<RecommendationQualityStats> {
  const response = await api.get<Record<string, unknown>>(
    "/recommendations/stats",
  );
  return mapStats(response.data);
}
